<template>
  <div :class="b()">
    <button
      :class="b('button')"
      @click="toggleDropdown"
      :title="'Текущая тема: ' + currentThemeLabel"
    >
      <span :class="b('label')">{{ currentThemeLabel }}</span>
      <span :class="[b('arrow'), { [b('arrow', 'open')]: isOpen }]">▼</span>
    </button>

    <transition name="dropdown">
      <div v-if="isOpen" :class="b('dropdown')">
        <button
          v-for="theme in availableThemes"
          :key="theme.value"
          :class="[
            b('theme-option'),
            { [b('theme-option', 'active')]: isThemeActive(theme.value) }
          ]"
          @click="selectTheme(theme.value)"
        >
          <span :class="b('theme-color', theme.value)"></span>
          <span :class="b('theme-label')">{{ theme.label }}</span>
          <span v-if="isThemeActive(theme.value)" :class="b('check')">✓</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, onUnmounted,
} from 'vue';
import { useTheme } from '@/composables/useTheme';
import bem from '@/utils/bem';

const b = bem('theme-toggle');

const {
  currentTheme,
  setTheme,
  getAvailableThemes,
  isThemeActive,
} = useTheme();

const isOpen = ref(false);
const availableThemes = computed(() => getAvailableThemes());

const currentThemeLabel = computed(() => {
  const theme = availableThemes.value.find((t) => t.value === currentTheme.value);
  return theme ? theme.label : 'Theme';
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const selectTheme = (themeName) => {
  setTheme(themeName);
  isOpen.value = false;
};

// Закрыть дропдаун при клике вне компонента
const handleClickOutside = ({ target }) => {
  const element = target.closest('.theme-toggle');
  if (!element && isOpen.value) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
@import 'src/styles/media.scss';

.theme-toggle {
  position: relative;
  z-index: 1000;

  &__button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
      padding: vw-full(8) vw-full(16);
      border-radius: vw-full(6);
      font-size: vw-full(14);
    }

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  &__icon {
    font-size: 18px;
    line-height: 1;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(18);
    }
  }

  &__label {
    @media (max-width: 768px) {
      display: none;
    }
  }

  &__arrow {
    font-size: 10px;
    transition: transform 0.3s ease;
    margin-left: 4px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(10);
      margin-left: vw-full(4);
    }

    &--open {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    min-width: 200px;
    background: var(--Background-Main);
    border: 1px solid var(--Border-Color);
    border-radius: 8px;
    box-shadow: var(--Shadow-Large);
    overflow: hidden;

    @include mediaMinMax(1025, $normal) {
      top: calc(100% + vw-full(8));
      min-width: vw-full(200);
      border-radius: vw-full(8);
    }
  }

  &__theme-option {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 16px;
    background: transparent;
    border: none;
    color: var(--Text-Primary);
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
    text-align: left;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
      padding: vw-full(12) vw-full(16);
      font-size: vw-full(14);
    }

    &:hover {
      background: var(--Background-Secondary);
    }

    &--active {
      background: var(--Primary);
      color: var(--Text-Inverse);
      font-weight: 600;

      &:hover {
        background: var(--Primary);
      }
    }

    &:not(:last-child) {
      border-bottom: 1px solid var(--Border-Light);
    }
  }

  &__theme-color {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid currentColor;
    flex-shrink: 0;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(20);
      height: vw-full(20);
      border: vw-full(2) solid currentColor;
    }

    &--default {
      background: linear-gradient(135deg, #ffffff 50%, #3d5a80 50%);
    }

    &--dark {
      background: linear-gradient(135deg, #1a1d23 50%, #98c1d9 50%);
    }

    &--blue {
      background: linear-gradient(135deg, #0d1b2a 50%, #778da9 50%);
    }

    &--green {
      background: linear-gradient(135deg, #1b4332 50%, #52b788 50%);
    }

    &--purple {
      background: linear-gradient(135deg, #3c096c 50%, #c77dff 50%);
    }
  }

  &__theme-label {
    flex: 1;
  }

  &__check {
    font-size: 16px;
    font-weight: bold;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }
  }
}

// Анимация дропдауна
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s ease;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-10px);

  @include mediaMinMax(1025, $normal) {
    transform: translateY(vw-full(-10));
  }
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);

  @include mediaMinMax(1025, $normal) {
    transform: translateY(vw-full(-10));
  }
}
</style>
